import React, { useMemo, useState } from "react";
import { Alert, Button, Col, Form, InputGroup, Modal, Nav, Row } from "react-bootstrap";
import { Link } from "react-router-dom";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import { createColumnHelper } from "@tanstack/react-table";
import { GenericTable } from "components/GenericTable";
import EditableCell from "containers/common/EditableCell";
import "containers/common/List.css";
import "containers/common/Table.css";
import { createElementIdFactory, createReusableTableElementIds } from "utils/elementIds";
import { PAGE_SIZES } from "utils/pageSizeConfig";

function EntityLeasingAgentList({
  isAuthenticated,
  user,
  data,
  updateTableState,
  saveRecord,
  getAgentsLeasing,
  getLeasingAgentContacts,
  leasingAgentOptions,
  leasingAgentContactOptions,
  validateRelatedRecord,
  propertyid,
  state,
  controlType,
  refreshDataSet,
  pageSize,
}) {
  const dataSet = "leasingAgents";
  const dataSetPrimaryKey = "leasing_agent_id";
  const tableData = Array.isArray(data) ? data : [];

  const [showAgentDialog, setShowAgentDialog] = useState(false);
  const [currentLeasingAgentIndex, setCurrentLeasingAgentIndex] = useState(null);
  const [agentSearchString, setAgentSearchString] = useState("");
  const [agentInterstateFlag, setAgentInterstateFlag] = useState(false);
  const [agentMenuIsOpen, setAgentMenuIsOpen] = useState(false);

  const [selectedAgent, setSelectedAgent] = useState(null);
  const [selectedAgentContact1, setSelectedAgentContact1] = useState(null);
  const [selectedAgentContact2, setSelectedAgentContact2] = useState(null);

  const [leasingAgentTelephone, setLeasingAgentTelephone] = useState("");
  const [leasingAgentFax, setLeasingAgentFax] = useState("");
  const [leasingAgentEmail, setLeasingAgentEmail] = useState("");
  const [leasingAgentInternet, setLeasingAgentInternet] = useState("");
  const [leasingAgentContact1Telephone, setLeasingAgentContact1Telephone] = useState("");
  const [leasingAgentContact1Email, setLeasingAgentContact1Email] = useState("");
  const [leasingAgentContact2Telephone, setLeasingAgentContact2Telephone] = useState("");
  const [leasingAgentContact2Email, setLeasingAgentContact2Email] = useState("");

  const [errorMessage, setErrorMessage] = useState("");
  const [showAlert, setShowAlert] = useState(false);

  const columnHelper = createColumnHelper();
  const createId = useMemo(() => createElementIdFactory("entity-leasing-agent-list"), []);
  const tableElementIds = useMemo(
    () => createReusableTableElementIds(createId, controlType || "propertyDetailForLeaseTable"),
    [createId, controlType]
  );

  const isSummaryMode = controlType === "leasingAgentTable" || controlType === "table2";
  const canAddRecords = !isSummaryMode;
  const resolvedPageSize =
    typeof pageSize === "number"
      ? pageSize
      : isSummaryMode
        ? PAGE_SIZES.entityDetailLeasingAgentsTab
        : PAGE_SIZES.propertyLeaseTab;

  const updateRowPatch = (rowIndex, patch) => {
    if (rowIndex === null || rowIndex < 0 || !tableData[rowIndex]) {
      return;
    }

    const localData = [...tableData];
    localData[rowIndex] = {
      ...localData[rowIndex],
      ...patch,
      modified: true,
      last_edited_by: user,
    };

    updateTableState(localData, dataSet);
  };

  const validateRecord = (record) => {
    if (typeof validateRelatedRecord !== "function") {
      return [true];
    }

    const validation = validateRelatedRecord(record, dataSet);
    return Array.isArray(validation) ? validation : [true];
  };

  const persistRecord = async (record) => {
    try {
      return await saveRecord(record, dataSet, dataSetPrimaryKey);
    } catch (err) {
      return { result: false, error_message: err?.message || "Save failed." };
    }
  };

  const handleInlineSave = async ({ row, column, value }) => {
    const liveRow = tableData[row.index] || row.original || {};
    const record = {
      ...liveRow,
      ...(column?.id ? { [column.id]: value } : {}),
      last_edited_by: user,
    };

    const validation = validateRecord(record);
    if (!validation[0]) {
      setErrorMessage(validation[1] || "Validation failed.");
      setShowAlert(true);
      return false;
    }

    const result = await persistRecord(record);
    if (!result || result.result === false) {
      setErrorMessage(result?.error_message || "Failed to save record.");
      setShowAlert(true);
      return false;
    }

    const localData = [...tableData];
    localData[row.index] = {
      ...localData[row.index],
      ...record,
      ...(typeof result === "object" ? result : {}),
      modified: false,
      new_record: false,
      ...(result?.id ? { [dataSetPrimaryKey]: result.id } : {}),
    };
    updateTableState(localData, dataSet);

    if (typeof refreshDataSet === "function") {
      await refreshDataSet(dataSet);
    }

    return true;
  };

  const handleDelete = async ({ row }) => {
    const confirmed = window.confirm("Are you sure you want to delete this record?");
    if (!confirmed) {
      return false;
    }

    const record = {
      ...(tableData[row.index] || row.original || {}),
      archived_date: new Date().toISOString(),
      last_edited_by: user,
    };

    const result = await persistRecord(record);
    if (!result || result.result === false) {
      setErrorMessage(result?.error_message || "Failed to delete record.");
      setShowAlert(true);
      return false;
    }

    const localData = tableData.filter((_, idx) => idx !== row.index);
    updateTableState(localData, dataSet);
    return true;
  };

  const addRecord = () => {
    if (!canAddRecords) {
      return;
    }

    const localData = [...tableData];
    localData.unshift({
      property_id: propertyid,
      classification_code: "",
      leasing_agent: "Click to enter",
      sort_order: 99,
      created_by: user,
      last_edited_by: user,
      new_record: true,
      modified: true,
      lease_preamble: "",
      lease_postamble: "",
      leasing_agent_contact_1: "",
      leasing_agent_contact_2: "",
    });

    updateTableState(localData, dataSet);
  };

  const viewAgentModal = async (row) => {
    const rowIndex = row.index;
    const current = tableData[rowIndex] || row.original || {};

    setCurrentLeasingAgentIndex(rowIndex);
    setSelectedAgent(
      current.entity_id_leasing_agent
        ? { value: current.entity_id_leasing_agent, label: current.leasing_agent }
        : null
    );
    setSelectedAgentContact1(
      current.entity_contact_id_leasing_agent_contact_1
        ? {
            value: current.entity_contact_id_leasing_agent_contact_1,
            label: current.leasing_agent_contact_1,
          }
        : null
    );
    setSelectedAgentContact2(
      current.entity_contact_id_leasing_agent_contact_2
        ? {
            value: current.entity_contact_id_leasing_agent_contact_2,
            label: current.leasing_agent_contact_2,
          }
        : null
    );

    setLeasingAgentTelephone(current.leasing_agent_telephone || "");
    setLeasingAgentFax(current.leasing_agent_fax || "");
    setLeasingAgentEmail(current.leasing_agent_email || "");
    setLeasingAgentInternet(current.leasing_agent_internet || "");
    setLeasingAgentContact1Telephone(current.leasing_agent_contact_1_telephone || "");
    setLeasingAgentContact1Email(current.leasing_agent_contact_1_email || "");
    setLeasingAgentContact2Telephone(current.leasing_agent_contact_2_telephone || "");
    setLeasingAgentContact2Email(current.leasing_agent_contact_2_email || "");

    if (current.entity_id_leasing_agent && typeof getLeasingAgentContacts === "function") {
      await getLeasingAgentContacts({ entityid: current.entity_id_leasing_agent });
    }

    setShowAgentDialog(true);
  };

  const handleAgentSearch = async () => {
    if (typeof getAgentsLeasing !== "function") {
      return;
    }

    const stateFilter = agentInterstateFlag ? null : state;
    await getAgentsLeasing({ name: agentSearchString, state: stateFilter });
    setAgentMenuIsOpen(true);
  };

  const handleEnterOnAgentSearch = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleAgentSearch();
    }
  };

  const handleAgentChange = async (selectedOption) => {
    setSelectedAgent(selectedOption || null);
    setAgentMenuIsOpen(false);

    if (!selectedOption) {
      setLeasingAgentTelephone("");
      setLeasingAgentFax("");
      setLeasingAgentEmail("");
      setLeasingAgentInternet("");
      setSelectedAgentContact1(null);
      setSelectedAgentContact2(null);
      setLeasingAgentContact1Telephone("");
      setLeasingAgentContact1Email("");
      setLeasingAgentContact2Telephone("");
      setLeasingAgentContact2Email("");

      updateRowPatch(currentLeasingAgentIndex, {
        entity_id_leasing_agent: null,
        leasing_agent: null,
        leasing_agent_telephone: null,
        leasing_agent_fax: null,
        leasing_agent_email: null,
        leasing_agent_internet: null,
        entity_contact_id_leasing_agent_contact_1: null,
        leasing_agent_contact_1: null,
        leasing_agent_contact_1_telephone: null,
        leasing_agent_contact_1_email: null,
        entity_contact_id_leasing_agent_contact_2: null,
        leasing_agent_contact_2: null,
        leasing_agent_contact_2_telephone: null,
        leasing_agent_contact_2_email: null,
      });
      return;
    }

    setLeasingAgentTelephone(selectedOption.telephone || "");
    setLeasingAgentFax(selectedOption.fax || "");
    setLeasingAgentEmail(selectedOption.email || "");
    setLeasingAgentInternet(selectedOption.internet || "");
    setSelectedAgentContact1(null);
    setSelectedAgentContact2(null);
    setLeasingAgentContact1Telephone("");
    setLeasingAgentContact1Email("");
    setLeasingAgentContact2Telephone("");
    setLeasingAgentContact2Email("");

    updateRowPatch(currentLeasingAgentIndex, {
      entity_id_leasing_agent: selectedOption.value,
      leasing_agent: selectedOption.label,
      leasing_agent_telephone: selectedOption.telephone || "",
      leasing_agent_fax: selectedOption.fax || "",
      leasing_agent_email: selectedOption.email || "",
      leasing_agent_internet: selectedOption.internet || "",
      entity_contact_id_leasing_agent_contact_1: null,
      leasing_agent_contact_1: null,
      leasing_agent_contact_1_telephone: null,
      leasing_agent_contact_1_email: null,
      entity_contact_id_leasing_agent_contact_2: null,
      leasing_agent_contact_2: null,
      leasing_agent_contact_2_telephone: null,
      leasing_agent_contact_2_email: null,
    });

    if (typeof getLeasingAgentContacts === "function") {
      await getLeasingAgentContacts({ entityid: selectedOption.value });
    }
  };

  const handleAgentContact1Change = (selectedOption) => {
    setSelectedAgentContact1(selectedOption || null);
    setLeasingAgentContact1Telephone(selectedOption?.telephone || "");
    setLeasingAgentContact1Email(selectedOption?.email || "");

    updateRowPatch(currentLeasingAgentIndex, {
      entity_contact_id_leasing_agent_contact_1: selectedOption?.value || null,
      leasing_agent_contact_1: selectedOption?.label || null,
      leasing_agent_contact_1_telephone: selectedOption?.telephone || null,
      leasing_agent_contact_1_email: selectedOption?.email || null,
    });
  };

  const handleAgentContact2Change = (selectedOption) => {
    setSelectedAgentContact2(selectedOption || null);
    setLeasingAgentContact2Telephone(selectedOption?.telephone || "");
    setLeasingAgentContact2Email(selectedOption?.email || "");

    updateRowPatch(currentLeasingAgentIndex, {
      entity_contact_id_leasing_agent_contact_2: selectedOption?.value || null,
      leasing_agent_contact_2: selectedOption?.label || null,
      leasing_agent_contact_2_telephone: selectedOption?.telephone || null,
      leasing_agent_contact_2_email: selectedOption?.email || null,
    });
  };

  const handleCreateContact1 = (label) => {
    const confirmed = window.confirm("Are you sure you want to create a contact for this agent?");
    if (!confirmed) {
      return;
    }

    const created = { value: "", label };
    setSelectedAgentContact1(created);
    setLeasingAgentContact1Telephone("");
    setLeasingAgentContact1Email("");

    updateRowPatch(currentLeasingAgentIndex, {
      entity_contact_id_leasing_agent_contact_1: "",
      leasing_agent_contact_1: label,
      leasing_agent_contact_1_telephone: "",
      leasing_agent_contact_1_email: "",
    });
  };

  const handleCreateContact2 = (label) => {
    const confirmed = window.confirm("Are you sure you want to create a contact for this agent?");
    if (!confirmed) {
      return;
    }

    const created = { value: "", label };
    setSelectedAgentContact2(created);
    setLeasingAgentContact2Telephone("");
    setLeasingAgentContact2Email("");

    updateRowPatch(currentLeasingAgentIndex, {
      entity_contact_id_leasing_agent_contact_2: "",
      leasing_agent_contact_2: label,
      leasing_agent_contact_2_telephone: "",
      leasing_agent_contact_2_email: "",
    });
  };

  const handleModalFieldChange = (field, value) => {
    const setters = {
      leasing_agent_telephone: setLeasingAgentTelephone,
      leasing_agent_fax: setLeasingAgentFax,
      leasing_agent_email: setLeasingAgentEmail,
      leasing_agent_internet: setLeasingAgentInternet,
      leasing_agent_contact_1_telephone: setLeasingAgentContact1Telephone,
      leasing_agent_contact_1_email: setLeasingAgentContact1Email,
      leasing_agent_contact_2_telephone: setLeasingAgentContact2Telephone,
      leasing_agent_contact_2_email: setLeasingAgentContact2Email,
    };

    if (setters[field]) {
      setters[field](value);
    }

    updateRowPatch(currentLeasingAgentIndex, { [field]: value });
  };

  const handleFormBlur = async () => {
    if (currentLeasingAgentIndex === null || !tableData[currentLeasingAgentIndex]) {
      return;
    }

    const current = tableData[currentLeasingAgentIndex];
    if (!current.modified) {
      return;
    }

    const validation = validateRecord(current);
    if (!validation[0]) {
      setErrorMessage(validation[1] || "Validation failed.");
      setShowAlert(true);
      return;
    }

    const result = await persistRecord({ ...current, last_edited_by: user });
    if (!result || result.result === false) {
      setErrorMessage(result?.error_message || "Failed to save record.");
      setShowAlert(true);
      return;
    }

    const localData = [...tableData];
    localData[currentLeasingAgentIndex] = {
      ...localData[currentLeasingAgentIndex],
      ...result,
      modified: false,
      new_record: false,
      ...(result?.id ? { [dataSetPrimaryKey]: result.id } : {}),
    };
    updateTableState(localData, dataSet);
  };

  const editableColumns = [
    columnHelper.accessor("leasing_agent_id", {
      id: "leasing_agent_id",
      header: "ID",
      size: 90,
      enableSorting: false,
      cell: (e) => (
        <Button variant="link" size="sm" onClick={() => viewAgentModal(e.row)}>
          {e.getValue()}
        </Button>
      ),
    }),
    columnHelper.accessor("lease_preamble", {
      id: "lease_preamble",
      header: "Lease Preamble",
      cell: EditableCell,
      size: 280,
      meta: { wrap: true },
      enableSorting: false,
    }),
    columnHelper.accessor("leasing_agent", {
      id: "leasing_agent",
      header: "Leasing Agent",
      size: 300,
      enableSorting: false,
      cell: (e) => (
        <div>
          <Button variant="link" size="sm" onClick={() => viewAgentModal(e.row)}>
            {e.getValue()}
          </Button>
          <div>p: {e.row.original.leasing_agent_telephone || ""}</div>
          <div>e: {e.row.original.leasing_agent_email || ""}</div>
          <div>i: {e.row.original.leasing_agent_internet || ""}</div>
        </div>
      ),
    }),
    columnHelper.accessor("lease_postamble", {
      id: "lease_postamble",
      header: "Lease Postamble",
      cell: EditableCell,
      size: 280,
      meta: { wrap: true },
      enableSorting: false,
    }),
    columnHelper.accessor("sort_order", {
      id: "sort_order",
      header: "Sort Order",
      cell: EditableCell,
      size: 110,
      meta: { numericOnly: true },
      enableSorting: false,
    }),
    columnHelper.display({
      id: "agent_details",
      header: "Contacts",
      size: 340,
      enableSorting: false,
      cell: (e) => (
        <div>
          <div>
            <b>Contact 1: </b>
            {e.row.original.leasing_agent_contact_1 || ""} t: {e.row.original.leasing_agent_contact_1_telephone || ""} e: {e.row.original.leasing_agent_contact_1_email || ""}
          </div>
          <div>
            <b>Contact 2: </b>
            {e.row.original.leasing_agent_contact_2 || ""} t: {e.row.original.leasing_agent_contact_2_telephone || ""} e: {e.row.original.leasing_agent_contact_2_email || ""}
          </div>
        </div>
      ),
    }),
    columnHelper.display({
      id: "delete",
      header: "",
      size: 90,
      enableSorting: false,
      cell: (e) => (
        <Button type="button" size="sm" variant="danger" onClick={() => handleDelete({ row: e.row })}>
          Delete
        </Button>
      ),
    }),
  ];

  const summaryColumns = [
    columnHelper.accessor("property_key", {
      id: "property_key",
      header: "Cityscope Reference",
      size: 150,
      cell: (e) => {
        const value = e.getValue();
        return value ? <Link to={`/properties/${value}`}>{value}</Link> : "";
      },
    }),
    columnHelper.accessor("lease_preamble", {
      id: "lease_preamble_summary",
      header: "Lease Preamble",
      size: 300,
      meta: { wrap: true },
    }),
    columnHelper.accessor("leasing_agent", {
      id: "leasing_agent_summary",
      header: "Leasing Agent",
      size: 240,
    }),
    columnHelper.accessor("lease_postamble", {
      id: "lease_postamble_summary",
      header: "Lease Postamble",
      size: 300,
      meta: { wrap: true },
    }),
    columnHelper.accessor("sort_order", {
      id: "sort_order_summary",
      header: "Sort Order",
      size: 110,
    }),
  ];

  return (
    <div className="LeasingAgentList">
      <Modal show={showAgentDialog} onHide={() => setShowAgentDialog(false)} dialogClassName="modal-90h">
        <Modal.Header closeButton>
          <Modal.Title>Agent</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ overflowX: "auto" }}>
          <Form>
            <Row>
              <Form.Group as={Col} controlId={createId("agent", "search-string")}>
                <Form.Label>Leasing Agent</Form.Label>
                <InputGroup size="sm">
                  <Form.Control
                    value={agentSearchString}
                    onChange={(e) => setAgentSearchString(e.target.value)}
                    onKeyDown={handleEnterOnAgentSearch}
                    type="text"
                    placeholder="Search entities"
                  />
                  <Button variant="outline-secondary" size="sm" onClick={handleAgentSearch} id={createId("agent", "search", "button")}>
                    Search
                  </Button>
                </InputGroup>

                <Form.Check
                  checked={agentInterstateFlag}
                  onChange={(e) => setAgentInterstateFlag(e.target.checked)}
                  label="Search interstate"
                  type="checkbox"
                />

                <Select
                  isClearable
                  value={selectedAgent}
                  onChange={handleAgentChange}
                  options={Array.isArray(leasingAgentOptions) ? leasingAgentOptions : []}
                  onBlur={handleFormBlur}
                  menuIsOpen={agentMenuIsOpen}
                  onMenuClose={() => setAgentMenuIsOpen(false)}
                />
              </Form.Group>

              <Form.Group as={Col} controlId={createId("leasing-agent", "telephone")}>
                <Form.Label>Telephone</Form.Label>
                <Form.Control
                  value={leasingAgentTelephone}
                  type="text"
                  onChange={(e) => handleModalFieldChange("leasing_agent_telephone", e.target.value)}
                  onBlur={handleFormBlur}
                />
              </Form.Group>

              <Form.Group as={Col} controlId={createId("leasing-agent", "fax")}>
                <Form.Label>Fax</Form.Label>
                <Form.Control
                  value={leasingAgentFax}
                  type="text"
                  onChange={(e) => handleModalFieldChange("leasing_agent_fax", e.target.value)}
                  onBlur={handleFormBlur}
                />
              </Form.Group>

              <Form.Group as={Col} controlId={createId("leasing-agent", "email")}>
                <Form.Label>Email</Form.Label>
                <Form.Control
                  value={leasingAgentEmail}
                  type="text"
                  onChange={(e) => handleModalFieldChange("leasing_agent_email", e.target.value)}
                  onBlur={handleFormBlur}
                />
              </Form.Group>

              <Form.Group as={Col} controlId={createId("leasing-agent", "internet")}>
                <Form.Label>Website</Form.Label>
                <Form.Control
                  value={leasingAgentInternet}
                  type="text"
                  onChange={(e) => handleModalFieldChange("leasing_agent_internet", e.target.value)}
                  onBlur={handleFormBlur}
                />
              </Form.Group>
            </Row>

            <Row>
              <Form.Group as={Col} controlId={createId("leasing-agent", "contact-1")}>
                <Form.Label>Contact 1</Form.Label>
                <CreatableSelect
                  isClearable
                  value={selectedAgentContact1}
                  onChange={handleAgentContact1Change}
                  options={Array.isArray(leasingAgentContactOptions) ? leasingAgentContactOptions : []}
                  onCreateOption={handleCreateContact1}
                  onBlur={handleFormBlur}
                />
              </Form.Group>

              <Form.Group as={Col} controlId={createId("leasing-agent", "contact-1", "telephone")}>
                <Form.Label>Telephone</Form.Label>
                <Form.Control
                  value={leasingAgentContact1Telephone}
                  type="text"
                  onChange={(e) => handleModalFieldChange("leasing_agent_contact_1_telephone", e.target.value)}
                  onBlur={handleFormBlur}
                />
              </Form.Group>

              <Form.Group as={Col} controlId={createId("leasing-agent", "contact-1", "email")}>
                <Form.Label>Email</Form.Label>
                <Form.Control
                  value={leasingAgentContact1Email}
                  type="email"
                  onChange={(e) => handleModalFieldChange("leasing_agent_contact_1_email", e.target.value)}
                  onBlur={handleFormBlur}
                />
              </Form.Group>
            </Row>

            <Row>
              <Form.Group as={Col} controlId={createId("leasing-agent", "contact-2")}>
                <Form.Label>Contact 2</Form.Label>
                <CreatableSelect
                  isClearable
                  value={selectedAgentContact2}
                  onChange={handleAgentContact2Change}
                  options={Array.isArray(leasingAgentContactOptions) ? leasingAgentContactOptions : []}
                  onCreateOption={handleCreateContact2}
                  onBlur={handleFormBlur}
                />
              </Form.Group>

              <Form.Group as={Col} controlId={createId("leasing-agent", "contact-2", "telephone")}>
                <Form.Label>Telephone</Form.Label>
                <Form.Control
                  value={leasingAgentContact2Telephone}
                  type="text"
                  onChange={(e) => handleModalFieldChange("leasing_agent_contact_2_telephone", e.target.value)}
                  onBlur={handleFormBlur}
                />
              </Form.Group>

              <Form.Group as={Col} controlId={createId("leasing-agent", "contact-2", "email")}>
                <Form.Label>Email</Form.Label>
                <Form.Control
                  value={leasingAgentContact2Email}
                  type="email"
                  onChange={(e) => handleModalFieldChange("leasing_agent_contact_2_email", e.target.value)}
                  onBlur={handleFormBlur}
                />
              </Form.Group>
            </Row>

            <Row>
              <Col>
                <Nav>
                  <Nav.Item>
                    <a href="/entities/new" target="_blank" rel="noreferrer">
                      <Button type="button" variant="primary" size="sm" id={createId("create-agent", "button")}>
                        Create Agent
                      </Button>
                    </a>
                  </Nav.Item>
                  {selectedAgent?.value ? (
                    <Nav.Item>
                      <a href={`/entities/${selectedAgent.value}`} target="_blank" rel="noreferrer">
                        <Button type="button" variant="primary" size="sm" id={createId("edit-agent", "button")}>
                          Edit Agent
                        </Button>
                      </a>
                    </Nav.Item>
                  ) : null}
                </Nav>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
      </Modal>

      <Modal show={showAlert} onHide={() => setShowAlert(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Uh Oh!</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="danger" className="mb-0">
            {errorMessage}
          </Alert>
        </Modal.Body>
      </Modal>

      {canAddRecords ? (
        <Row>
          <Col className="text-right" style={{ marginBottom: "12px" }}>
            <Button variant="secondary" size="sm" onClick={addRecord} id={createId("add", "button")}>
              Add
            </Button>
          </Col>
        </Row>
      ) : null}

      <Row className="show-Container">
        <Col sm={12} md={12} lg={12}>
          {isAuthenticated ? (
            <GenericTable
              section="leasing-agents"
              columns={isSummaryMode ? summaryColumns : editableColumns}
              data={tableData}
              pageSize={resolvedPageSize}
              elementIds={tableElementIds}
              columnResizeMode="onChange"
              meta={{
                updateData: (rowIndex, columnId, value) => {
                  const localData = [...tableData];
                  if (!localData[rowIndex]) {
                    return;
                  }

                  localData[rowIndex] = {
                    ...localData[rowIndex],
                    [columnId]: value,
                    modified: true,
                    last_edited_by: user,
                  };

                  updateTableState(localData, dataSet);
                },
                handleSave: handleInlineSave,
                handleDelete,
              }}
            />
          ) : null}
        </Col>
      </Row>
    </div>
  );
}

export default EntityLeasingAgentList;
